/**
 * Keeps the map camera centered on a selected vehicle while follow mode is active.
 * Camera updates are driven by the vehicle custom layer frame callback (same position as the sprite).
 */

import maplibregl from "maplibre-gl";
import { useCallback, useEffect, useRef, type RefObject } from "react";
import { type VehicleCustomLayer } from "../components/MapLibre/VehicleCustomLayer";
import { isValidVehicleSelectionLngLat } from "../components/MapLibre/vehicleSelection/vehicleSelection.types";
import {
    ephemeralStore,
    getMapInstance,
    mapReadySlice,
    navigationSessionSlice,
    useEphemeralSelector,
} from "../store/ephemeral";

/** Minimum center delta (degrees) before jumpTo; avoids redundant camera work when idle (~0.05 m). */
const VEHICLE_FOLLOW_MIN_JUMP_DELTA_DEGREES = 0.0000005;

export interface UseMapVehicleFollowParams {

    /** Live vehicle coordinates for the initial snap when follow is toggled on. */
    "vehicleLngLatRef": RefObject<[number, number] | null>;

    /** Whether a vehicle is currently selected with valid coordinates. */
    "hasValidSelection": boolean;
}

export interface UseMapVehicleFollowResult {

    /**
     * Wires frame-synced follow on a vehicle custom layer instance.
     * Must be called whenever a new layer is created (initial load or style switch).
     *
     * @param layer Vehicle custom layer instance, or undefined to skip.
     */
    "bindVehicleFollowLayer": ( layer: VehicleCustomLayer | undefined ) => void;
}

/**
 * Clears vehicle follow mode in the navigation session slice.
 */
function clearVehicleFollow(): void {
    ephemeralStore.setValue( navigationSessionSlice, ( previous ) => {
        if ( !previous.isFollowingVehicle ) {
            return previous;
        }

        return {
            ...previous,
            "isFollowingVehicle": false,
        };
    } );
}

/**
 * Returns whether the map center should jump to the target.
 *
 * @param map Target map instance.
 * @param center Candidate center.
 * @returns True when the center differs from the current map center beyond the deadband.
 */
function shouldJumpMapCenter( map: maplibregl.Map, center: [number, number] ): boolean {
    const currentCenter = map.getCenter();

    return Math.abs( currentCenter.lng - center[0] ) > VEHICLE_FOLLOW_MIN_JUMP_DELTA_DEGREES ||
      Math.abs( currentCenter.lat - center[1] ) > VEHICLE_FOLLOW_MIN_JUMP_DELTA_DEGREES;
}

/**
 * Registers frame-synced camera lock and user-gesture listeners for vehicle follow mode.
 *
 * @param params Hook parameters.
 * @returns Layer binding handle for MapComponent lifecycle hooks.
 */
export function useMapVehicleFollow( params: UseMapVehicleFollowParams ): UseMapVehicleFollowResult {
    const { vehicleLngLatRef, hasValidSelection } = params;

    const isMapReady = useEphemeralSelector( mapReadySlice, ( ready ) => ready.isMapReady );
    const isFollowingVehicle = useEphemeralSelector(
        navigationSessionSlice,
        ( session ) => session.isFollowingVehicle,
    ) === true;

    const wasFollowingVehicleRef = useRef( false );
    const isApplyingFollowCameraRef = useRef( false );
    const boundLayerRef = useRef<VehicleCustomLayer | undefined>( undefined );

    /**
     * Jumps the map center only; zoom, bearing, and pitch are preserved by MapLibre.
     *
     * @param center Target center coordinates.
     */
    const jumpMapCenterOnly = useCallback( ( center: [number, number] ): void => {
        const activeMap = getMapInstance();
        if ( activeMap === undefined || !shouldJumpMapCenter( activeMap, center ) ) {
            return;
        }

        isApplyingFollowCameraRef.current = true;
        activeMap.jumpTo( { "center": new maplibregl.LngLat( center[0], center[1] ) } );
        requestAnimationFrame( () => {
            isApplyingFollowCameraRef.current = false;
        } );
    }, [] );

    const bindVehicleFollowLayer = useCallback( ( layer: VehicleCustomLayer | undefined ): void => {
        if ( boundLayerRef.current !== undefined ) {
            boundLayerRef.current.onSelectedVehicleFramePosition = null;
            boundLayerRef.current = undefined;
        }

        if ( layer === undefined ) {
            return;
        }

        boundLayerRef.current = layer;
        layer.onSelectedVehicleFramePosition = ( lngLat: [number, number] ): void => {
            const following = ephemeralStore.peek( navigationSessionSlice ).isFollowingVehicle === true;
            if ( !following || !isValidVehicleSelectionLngLat( lngLat ) ) {
                return;
            }

            jumpMapCenterOnly( lngLat );
        };
    }, [ jumpMapCenterOnly ] );

    /** Initial center snap when follow mode is enabled. */
    useEffect( () => {
        const wasFollowing = wasFollowingVehicleRef.current;
        wasFollowingVehicleRef.current = isFollowingVehicle;

        if ( !isFollowingVehicle || wasFollowing || !hasValidSelection ) {
            return;
        }

        const lngLat = vehicleLngLatRef.current;
        if ( lngLat === null || !isValidVehicleSelectionLngLat( lngLat ) ) {
            return;
        }

        jumpMapCenterOnly( lngLat );
    }, [ hasValidSelection, isFollowingVehicle, jumpMapCenterOnly, vehicleLngLatRef ] );

    useEffect( () => () => {
        bindVehicleFollowLayer( undefined );
    }, [ bindVehicleFollowLayer ] );

    /** Drop follow when the selected vehicle is cleared. */
    useEffect( () => {
        if ( !hasValidSelection && isFollowingVehicle ) {
            clearVehicleFollow();
        }
    }, [ hasValidSelection, isFollowingVehicle ] );

    /** Stop follow on user-initiated pan, rotate, or pitch (zoom alone keeps follow active). */
    useEffect( () => {
        const map = getMapInstance();
        if ( map === undefined || !isMapReady ) {
            return;
        }

        const handleUserGestureStop = (): void => {
            if ( isApplyingFollowCameraRef.current ) {
                return;
            }
            if ( ephemeralStore.peek( navigationSessionSlice ).isFollowingVehicle ) {
                clearVehicleFollow();
            }
        };

        map.on( "dragstart", handleUserGestureStop );
        map.on( "rotatestart", handleUserGestureStop );
        map.on( "pitchstart", handleUserGestureStop );

        return () => {
            map.off( "dragstart", handleUserGestureStop );
            map.off( "rotatestart", handleUserGestureStop );
            map.off( "pitchstart", handleUserGestureStop );
        };
    }, [ isMapReady ] );

    return { bindVehicleFollowLayer };
}
